<template>
  <v-dialog :value="value" fullscreen hide-overlay transition="dialog-bottom-transition" @input="close">
    <v-card class="acq-dialog ms-dialog d-flex flex-column" tile>
      <!-- Header -->
      <div class="acq-header d-flex align-center px-4 px-md-6" style="gap: 12px">
        <span class="ms-head-icon"><v-icon>mdi-map-search-outline</v-icon></span>
        <div class="d-flex flex-column" style="min-width: 0">
          <span class="text-subtitle-1 font-weight-medium">Kartensuche</span>
          <span class="acq-tiny d-none d-sm-block">Standorte oder Lehrkräfte rund um einen beliebigen Ort finden</span>
        </div>
        <v-spacer/>
        <v-btn aria-label="Dialog schließen" icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="acq-body ms-body">
        <!-- Filters -->
        <aside class="acq-panel ms-panel soft-inputs">
          <div class="ms-panel__scroll">
            <segmented-control :items="kindItems" :value="kind" block label="Was suchst du?" @input="setKind"/>

            <section class="ms-sec">
              <div class="section-title">Ausgangspunkt</div>
              <!-- V2: start at a Standort or at the home of a teacher -->
              <segmented-control :items="originItems" :value="originMode" block label="Ausgangspunkt" @input="setOriginMode"/>
              <div v-if="originMode === 'address'" class="mdlg-field">
                <span class="acq-label">Adresse oder Ort <span class="mdlg-req">*</span></span>
                <create-address v-model="address" :allow-manual-input="false" :default-object="pinnedAddress" :require-street="false" :use-osm="false" color="info" label=""
                                prepend-icon="" soft/>
              </div>
              <div v-else-if="originMode === 'institution'" class="mdlg-field">
                <span class="acq-label">Standort <span class="mdlg-req">*</span></span>
                <institution-autocomplete v-model="originInstitution" :rounded="false" color="info" hide-details/>
              </div>
              <div v-else class="mdlg-field">
                <span class="acq-label">Lehrkraft (Wohnort) <span class="mdlg-req">*</span></span>
                <employee-autocomplete v-model="originEmployee" :filter="{isTeacher: true}" :rounded="false" color="info" hide-details/>
              </div>
              <div v-if="originError" class="acq-tiny error--text">{{ originError }}</div>
              <div v-else class="acq-tiny">Den Pin auf der Karte kannst du verschieben, die Suche läuft dann neu.</div>
            </section>

            <section class="ms-sec">
              <div class="section-title">Suchgebiet</div>
              <!-- V4: free area instead of the radius (Standorte only) -->
              <segmented-control :items="searchModeItems" :value="isArea ? 'area' : 'radius'" block label="Art des Suchgebiets" @input="setSearchMode"/>
              <template v-if="!isArea">
                <div class="d-flex align-baseline justify-space-between">
                  <span class="acq-label">Umkreis (Luftlinie)</span>
                  <span class="text-h6 info--text text--darken-3">{{ radiusText }} km</span>
                </div>
                <v-slider v-model="radiusKm" :max="radiusMax" :min="radiusMin" class="mt-n2" color="info" dense hide-details step="2.5"
                          track-color="blue lighten-4"/>
                <div class="d-flex justify-space-between acq-tiny">
                  <span>{{ radiusMin }} km</span>
                  <span>{{ radiusMax }} km</span>
                </div>
              </template>
              <template v-else>
                <div :class="areaProblem && areaDraft.closed ? 'area-hint--warn' : 'area-hint--info'" class="area-hint text-caption" role="status">
                  {{ areaProblem || 'Fertig. Punkte lassen sich weiter verschieben, ein Doppelklick löscht einen Punkt.' }}
                </div>
                <div class="d-flex flex-wrap" style="gap: 6px">
                  <button :disabled="!areaDraft.points.length" class="cta cta--ghost cta--sm" type="button" @click="areaDraft = undoLast(areaDraft)">
                    <v-icon class="cta__icon" small>mdi-undo</v-icon>
                    Letzten Punkt entfernen
                  </button>
                  <button :disabled="!areaDraft.points.length" class="cta cta--ghost cta--sm" type="button" @click="areaDraft = emptyDraft()">
                    <v-icon class="cta__icon" small>mdi-eraser</v-icon>
                    Neu zeichnen
                  </button>
                  <button :disabled="!originPlace" class="cta cta--ghost cta--sm" type="button" @click="startFromCircle">
                    <v-icon class="cta__icon" small>mdi-octagon-outline</v-icon>
                    Aus Umkreis starten ({{ radiusText }} km)
                  </button>
                </div>
                <div class="acq-tiny">Klicke Punkte auf die Karte, ein Klick auf den ersten Punkt schließt die Fläche. Die Entfernung bleibt Luftlinie ab dem Ausgangspunkt.</div>
              </template>
            </section>

            <section class="ms-sec">
              <div class="section-title">Verkehrsmittel</div>
              <segmented-control :items="travelModeItems" :value="travelMode" block label="Verkehrsmittel" tall @input="setTravelMode"/>
              <div class="acq-tiny">
                {{ kind === 'employee' && travelMode === 'own' ? '„Je Lehrkraft“: Auto, wenn im Profil hinterlegt, sonst ÖPNV.' : 'Gilt für die Fahrzeiten. Der Umkreis selbst ist Luftlinie.' }}
              </div>
            </section>

            <!-- Standorte -->
            <section v-if="kind === 'institution'" class="ms-sec">
              <div class="section-title">Filter</div>
              <template v-if="access.institutionActive">
                <span class="acq-label">Status</span>
                <segmented-control v-model="institutionFilter.isActive" :items="activeItems" block label="Status"/>
              </template>
              <template v-if="access.institutionType">
                <span class="acq-label">Art des Standorts</span>
                <pill-group v-model="institutionFilter.type" :items="institutionTypes" label="Art des Standorts"/>
              </template>
              <template v-if="access.coverage">
                <span class="acq-label">Lehrkraft am Standort</span>
                <pill-group v-model="coverageFilter" :items="coverageItems" label="Lehrkraft am Standort"/>
              </template>
              <details v-if="access.acquisitionStatus" class="ms-more">
                <summary>Weitere Filter <span class="ms-more__count">{{ institutionFilter.acquisitionStatus.length }}</span></summary>
                <div class="ms-more__body">
                  <span class="acq-label">Akquisestatus</span>
                  <pill-group v-model="institutionFilter.acquisitionStatus" :items="acquisitionStatusItems" label="Akquisestatus"/>
                </div>
              </details>
            </section>

            <!-- Lehrkräfte -->
            <section v-else class="ms-sec">
              <div class="section-title">Filter</div>
              <template v-if="access.employeeActive">
                <span class="acq-label">Status</span>
                <segmented-control v-model="employeeFilter.isActive" :items="activeItems" block label="Status"/>
              </template>
              <div v-if="access.subjects" class="mdlg-field">
                <span class="acq-label">Unterrichtete Fächer</span>
                <subject-autocomplete v-model="employeeFilter.subjects" :rounded="false" color="info" hide-details multiple/>
              </div>
              <template v-if="access.hasCar">
                <span class="acq-label">Fahrzeug</span>
                <segmented-control v-model="employeeFilter.hasCar" :items="carItems" block label="Fahrzeug"/>
              </template>
              <details v-if="hasMoreEmployeeFilters" class="ms-more">
                <summary>Weitere Filter <span class="ms-more__count">{{ moreEmployeeFilterCount }}</span></summary>
                <div class="ms-more__body">
                  <template v-if="access.driverLicense">
                    <span class="acq-label">Führerschein</span>
                    <segmented-control v-model="employeeFilter.hasDriverLicense" :items="driverLicenseItems" block label="Führerschein"/>
                  </template>
                  <template v-if="access.hasLogin">
                    <span class="acq-label">Vanilla-Zugang</span>
                    <segmented-control v-model="employeeFilter.hasLogin" :items="loginItems" block label="Vanilla-Zugang"/>
                  </template>
                  <div v-if="access.assignedInstitutions" class="mdlg-field">
                    <span class="acq-label">Zugeteilte Standorte</span>
                    <institution-autocomplete v-model="employeeFilter.assignedInstitutions" :rounded="false" color="info" hide-details multiple/>
                  </div>
                  <div v-if="access.plannedInstitutions" class="mdlg-field">
                    <span class="acq-label">Vorgemerkte Standorte</span>
                    <institution-autocomplete v-model="employeeFilter.plannedInstitutions" :rounded="false" color="info" hide-details multiple/>
                  </div>
                  <div v-if="access.acquiredBy" class="mdlg-field">
                    <span class="acq-label">Akquiriert von</span>
                    <employee-autocomplete v-model="employeeFilter.acquiredBy" :rounded="false" color="info" hide-details multiple/>
                  </div></div>
              </details>
            </section>
          </div>
          <div class="ms-panel__foot">
            <button :aria-busy="String(loading)" :disabled="!canSearch || loading" v-tip="searchBlockedReason" class="cta cta--primary" style="width: 100%"
                    type="button" @click="search">
              <v-progress-circular v-if="loading" indeterminate size="14" width="2"/>
              <v-icon v-else class="cta__icon" small>mdi-magnify</v-icon>
              {{ searched ? 'Erneut suchen' : 'Suchen' }}
            </button>
            <div class="acq-tiny text-center">Sucht erst per Luftlinie. Fahrzeiten berechnest du danach auf Knopfdruck.</div>
          </div>
        </aside>

        <!-- Map -->
        <div class="ms-map-wrap">
          <map-search-map :highlight-id="highlightId" :kind="kind" :origin-label="originLabel" :origin-place="originPlace" :radius-km="radiusKm"
                          :area="isArea ? areaDraft : null" :rows="sortedRows" :searched="searched" @hover="highlightId = $event" @move="pinMoved"
                          @select="selectFromMap" @update:area="areaDraft = $event"/>
        </div>

        <!-- Results -->
        <aside class="ms-results">
          <map-search-results ref="results" :calculating="calculating" :can-calculate="canUseDistanceMatrix" :exact-done="exactDone"
                              :highlight-id="highlightId" :kind="kind" :limit="exactLimit" :loading="loading" :origin="originPlace"
                              :radius-km="radiusKm" :routes="currentRoutes" :rows="sortedRows" :searched="searched" :sort-by.sync="sortBy"
                              :area-mode="isArea" :can-create-list="canCreateList" :travel-mode="travelMode" :travel-mode-noun="travelModeNoun"
                              @calculate="calculateExact" @create-list="openCreateList" @hover="highlightId = $event" @open="openCard"/>
        </aside>
      </div>
    </v-card>

    <!-- DIALOGS -->
    <institution-card-dialog v-if="cardInstitutionId" v-model="showInstitutionCard" :institution-id="cardInstitutionId"/>
    <employee-card-dialog v-if="cardEmployeeId" v-model="showEmployeeCard" :employee-id="cardEmployeeId"/>
    <create-acquisition-list-dialog v-if="createListUsed" v-model="showCreateList" :preset-area="presetArea"/>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import {Place} from '@/interfaces/place.interface';
import {GoogleTravelModesEnum} from '@/classes/api/google/GoogleTravelModes.enum';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {GeocoderAPI} from '@/classes/api/geocoder.api.class';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {InstitutionFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/InstitutionFields';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {CustomAccessKeys} from '@/classes/clientOnly/permissionTreeResources/enums/CustomAccessKeys';
import {InstitutionTypes, InstitutionTypesVue} from '@/enums/InstitutionTypes.enum';
import {AcquisitionListStatusEnum} from '@/enums/institutioner/acquisition/AcquisitionListStatus.enum';
import {STATUS_ORDER, statusLabel} from '@/components/acquisitionList/ui/acquisitionStatusStyle';
import SegmentedControl, {SegmentItem} from '@/components/common/modern/segmentedControl.vue';
import PillGroup, {PillItem} from '@/components/common/modern/pillGroup.vue';
import CreateAddress from '@/components/common/create/createAddress.vue';
import InstitutionAutocomplete from '@/components/common/search-fields/institutionAutocomplete.vue';
import EmployeeAutocomplete from '@/components/common/search-fields/employeeAutocomplete.vue';
import SubjectAutocomplete from '@/components/common/search-fields/subjectAutocomplete.vue';
import MapSearchMap from '@/components/app/mapSearch/mapSearchMap.vue';
import MapSearchResults from '@/components/app/mapSearch/mapSearchResults.vue';
import {GeoJsonPolygon} from '@/helpers/geoPolygon.helper';
import {AreaDraft, draftFromCircle, draftPolygon, draftProblem, emptyDraft, undoLast} from '@/components/acquisitionList/create/areaDraw.helper';
import {canCreateAcquisitionList} from '@/components/acquisitionList/create/acquisitionListCreateAccess';
import {
  addressLine,
  Coverage,
  COVERAGE_ITEMS,
  employeeRow,
  EXACT_LIMIT,
  institutionRow,
  MAP_TRAVEL_MODES,
  MapRow,
  MapSearchKind,
  MapSortKey,
  MapTravelMode,
  MapSearchPreference,
  OriginMode,
  RADIUS_MAX_KM,
  RADIUS_MIN_KM,
  RouteInfo,
  RowAccess,
  sortRows,
  TRI_STATE,
  readMapSearchPreference,
  writeMapSearchPreference,
} from '@/components/app/mapSearch/mapSearch.helper';

type ActiveChoice = true | false | 'all';

/** Waits this long after a filter change before searching again (only after a first search) */
const RESEARCH_DELAY_MS = 450;

/**
 * Kartensuche: Standorte or teachers around an address, a Standort or the home of a teacher.
 * Searches by air-line first; routes from Google only on request ("Berechnen", at most EXACT_LIMIT results).
 */
export default Vue.extend({
  components: {
    SegmentedControl,
    PillGroup,
    CreateAddress,
    InstitutionAutocomplete,
    EmployeeAutocomplete,
    SubjectAutocomplete,
    MapSearchMap,
    MapSearchResults,
    InstitutionCardDialog: () => import('@/components/common/profile/institutionCardDialog.vue'),
    EmployeeCardDialog: () => import('@/components/common/profile/employeeCardDialog.vue'),
    CreateAcquisitionListDialog: () => import('@/components/acquisitionList/createAcquisitionListDialog.vue'),
  },
  props: {
    value: {type: Boolean},
  },
  data: () => ({
    kind: 'institution' as MapSearchKind,
    originMode: 'address' as OriginMode,
    address: {} as Place,
    /** Address of the moved pin, shown in the address field */
    pinnedAddress: null as Place | null,
    originInstitution: undefined as Types.ObjectId | undefined,
    originEmployee: undefined as Types.ObjectId | undefined,
    /** Address of the chosen Standort / teacher */
    originEntity: null as null | { id: string; type: CrudEntityTypes.INSTITUTION | CrudEntityTypes.EMPLOYEE; name: string; address: Place },
    originError: '',
    radiusKm: 10,
    searchMode: 'radius' as 'radius' | 'area',
    areaDraft: emptyDraft() as AreaDraft,
    showCreateList: false,
    /** The create dialog is only mounted once it was opened */
    createListUsed: false,
    presetArea: null as GeoJsonPolygon | null,
    preferencesLoaded: false,
    /** A search ran in this dialog; a new complete origin / area then searches again by itself */
    hadSearched: false,
    institutionTravelMode: GoogleTravelModesEnum.DRIVING as MapTravelMode,
    employeeTravelMode: 'own' as MapTravelMode,
    institutionFilter: {
      isActive: true as ActiveChoice,
      type: [InstitutionTypes.PUBLICSCHOOL] as InstitutionTypes[],
      acquisitionStatus: [] as AcquisitionListStatusEnum[],
    },
    coverageFilter: [] as Coverage[],
    employeeFilter: {
      isActive: true as ActiveChoice,
      subjects: [] as Types.ObjectId[],
      hasCar: null as boolean | null,
      hasDriverLicense: null as boolean | null,
      hasLogin: null as boolean | null,
      assignedInstitutions: [] as Types.ObjectId[],
      plannedInstitutions: [] as Types.ObjectId[],
      acquiredBy: [] as Types.ObjectId[],
    },
    rows: [] as MapRow[],
    searched: false,
    loading: false,
    calculating: false,
    searchRun: 0,
    researchTimer: undefined as number | undefined,
    /** Route infos per origin + travel mode; null = Google found no route */
    routes: {} as Record<string, Record<string, RouteInfo | null>>,
    sortBy: 'air' as MapSortKey,
    highlightId: null as string | null,
    cardInstitutionId: null as Types.ObjectId | null,
    cardEmployeeId: null as Types.ObjectId | null,
    showInstitutionCard: false,
    showEmployeeCard: false,
    radiusMin: RADIUS_MIN_KM,
    radiusMax: RADIUS_MAX_KM,
    exactLimit: EXACT_LIMIT,
  }),
  computed: {
    access(): Record<string, boolean> {
      const crud = this.$$crudAccessManager;
      const inst = (...keys: string[]) => crud.canReadAllProvided(CrudEntityTypes.INSTITUTION, ...keys);
      const emp = (...keys: string[]) => crud.canReadAllProvided(CrudEntityTypes.EMPLOYEE, ...keys);
      return {
        institutions: inst(InstitutionFields.NAME, InstitutionFields.ADDRESS),
        employees: emp(EmployeeFields.FIRST_NAME, EmployeeFields.LAST_NAME, EmployeeFields.ADDRESS, EmployeeFields.IS_TEACHER),
        institutionActive: inst(InstitutionFields.IS_ACTIVE),
        institutionType: inst(InstitutionFields.TYPE),
        // assignedTeachers / plannedTeachers are computed by the institutioner from the teachers' assignments
        coverage: emp(EmployeeFields.ASSIGNED_INSTITUTIONS, EmployeeFields.PLANNED_INSTITUTIONS),
        acquisitionStatus: inst(InstitutionFields.ACQUISITION_STATUS),
        employeeActive: emp(EmployeeFields.IS_ACTIVE),
        subjects: emp(EmployeeFields.SUBJECTS) && crud.canReadAllProvided(CrudEntityTypes.SUBJECT, 'name'),
        hasCar: emp(EmployeeFields.HAS_CAR),
        driverLicense: emp(EmployeeFields.HAS_DRIVER_LICENSE),
        hasLogin: emp(EmployeeFields.HAS_LOGIN),
        assignedInstitutions: emp(EmployeeFields.ASSIGNED_INSTITUTIONS) && inst(InstitutionFields.NAME),
        plannedInstitutions: emp(EmployeeFields.PLANNED_INSTITUTIONS) && inst(InstitutionFields.NAME),
        acquiredBy: emp(EmployeeFields.ACQUIRED_BY),
        assignedCount: emp(EmployeeFields.ASSIGNED_INSTITUTIONS),
        maxDistance: emp(EmployeeFields.MAX_DISTANCE),
      };
    },
    isArea(): boolean {
      return this.kind === 'institution' && this.searchMode === 'area';
    },
    areaPolygon(): GeoJsonPolygon | null {
      return this.isArea ? draftPolygon(this.areaDraft) : null;
    },
    areaProblem(): string | null {
      return draftProblem(this.areaDraft);
    },
    searchModeItems(): SegmentItem[] {
      return [
        {value: 'radius', text: 'Umkreis', icon: 'mdi-radius-outline'},
        {value: 'area', text: 'Bereich zeichnen', icon: 'mdi-vector-polygon', disabled: this.kind !== 'institution', reason: 'Bereich zeichnen gibt es nur für Standorte'},
      ];
    },
    canSearch(): boolean {
      return !!this.originPlace && (!this.isArea || !!this.areaPolygon);
    },
    searchBlockedReason(): string {
      if (!this.originPlace) {
        return 'Erst einen Ausgangspunkt wählen';
      }
      return this.isArea && !this.areaPolygon ? 'Erst den Bereich fertig zeichnen' : '';
    },
    canCreateList(): boolean {
      return this.kind === 'institution' && canCreateAcquisitionList();
    },
    preference(): MapSearchPreference {
      const i = this.institutionFilter;
      const e = this.employeeFilter;
      return {
        kind: this.kind,
        originMode: this.originMode,
        searchMode: this.searchMode,
        radiusKm: this.radiusKm,
        institutionTravelMode: this.institutionTravelMode,
        employeeTravelMode: this.employeeTravelMode,
        institutionActive: i.isActive,
        institutionTypes: [...i.type],
        acquisitionStatus: [...i.acquisitionStatus],
        coverage: [...this.coverageFilter],
        employeeActive: e.isActive,
        subjects: e.subjects.map(String),
        hasCar: e.hasCar,
        hasDriverLicense: e.hasDriverLicense,
        hasLogin: e.hasLogin,
      };
    },
    canUseDistanceMatrix(): boolean {
      return !!this.$$crudAccessManager.getCustom(CustomAccessKeys.USE_GMAPS_DISTANCE_MATRIX);
    },
    kindItems(): SegmentItem[] {
      return [
        {value: 'institution', text: 'Standorte', icon: 'mdi-school-outline', disabled: !this.access.institutions, reason: 'Kein Lesezugriff auf Name und Adresse von Standorten'},
        {value: 'employee', text: 'Lehrkräfte', icon: 'mdi-account-music-outline', disabled: !this.access.employees, reason: 'Kein Lesezugriff auf Name und Adresse von Lehrkräften'},
      ];
    },
    originItems(): SegmentItem[] {
      return [
        {value: 'address', text: 'Adresse', icon: 'mdi-map-marker-outline'},
        {value: 'institution', text: 'Standort', icon: 'mdi-school-outline', disabled: !this.access.institutions, reason: 'Kein Lesezugriff auf Standorte'},
        {value: 'employee', text: 'Lehrkraft', icon: 'mdi-home-outline', disabled: !this.access.employees, reason: 'Kein Lesezugriff auf Lehrkräfte'},
      ];
    },
    travelMode(): MapTravelMode {
      return this.kind === 'institution' ? this.institutionTravelMode : this.employeeTravelMode;
    },
    travelModeItems(): SegmentItem[] {
      return MAP_TRAVEL_MODES
          .filter((mode) => mode.value !== 'own' || this.kind === 'employee')
          .map((mode) => ({value: mode.value, text: mode.text, icon: mode.icon}));
    },
    travelModeNoun(): string {
      return MAP_TRAVEL_MODES.find((mode) => mode.value === this.travelMode)?.noun || '';
    },
    activeItems(): SegmentItem[] {
      return [{value: true, text: 'Aktiv'}, {value: false, text: 'Inaktiv'}, {value: 'all', text: 'Alle'}];
    },
    carItems: () => TRI_STATE('Mit Auto', 'Ohne Auto') as SegmentItem[],
    driverLicenseItems: () => TRI_STATE('Ja', 'Nein') as SegmentItem[],
    loginItems: () => TRI_STATE('Ja', 'Nein') as SegmentItem[],
    institutionTypes: () => InstitutionTypesVue as PillItem[],
    coverageItems: () => COVERAGE_ITEMS as PillItem[],
    acquisitionStatusItems: () => STATUS_ORDER.map((status) => ({value: status, text: statusLabel(status)})) as PillItem[],
    hasMoreEmployeeFilters(): boolean {
      const a = this.access;
      return a.driverLicense || a.hasLogin || a.assignedInstitutions || a.plannedInstitutions || a.acquiredBy;
    },
    moreEmployeeFilterCount(): number {
      const f = this.employeeFilter;
      return [f.hasDriverLicense !== null, f.hasLogin !== null, f.assignedInstitutions.length > 0, f.plannedInstitutions.length > 0, f.acquiredBy.length > 0]
          .filter(Boolean).length;
    },
    radiusText(): string {
      return this.radiusKm.toLocaleString('de-DE');
    },
    /** The place the search starts from (address field / moved pin, or the chosen Standort / teacher) */
    originPlace(): Place | null {
      const place = this.originMode === 'address' ? this.address : this.originEntity?.address;
      return place?.location?.coordinates?.length === 2 ? place : null;
    },
    originLabel(): string {
      if (this.originMode !== 'address' && this.originEntity) {
        return this.originEntity.name;
      }
      return addressLine(this.originPlace) || 'Markierte Position';
    },
    /** Key of the route cache: origin + kind + travel mode */
    routeKey(): string {
      const coords = this.originPlace?.location?.coordinates || [];
      const origin = this.originMode !== 'address' && this.originEntity ? this.originEntity.id : coords.join(',');
      return `${this.kind}|${origin}|${this.travelMode}`;
    },
    currentRoutes(): Record<string, RouteInfo | null> {
      return this.routes[this.routeKey] || {};
    },
    visibleRows(): MapRow[] {
      if (this.kind !== 'institution' || !this.coverageFilter.length) {
        return this.rows;
      }
      return this.rows.filter((row) => row.coverage && this.coverageFilter.includes(row.coverage));
    },
    sortedRows(): MapRow[] {
      return sortRows(this.visibleRows, this.sortBy, this.currentRoutes);
    },
    /** The nearest EXACT_LIMIT results (by air-line) have route infos for the current origin and travel mode */
    exactDone(): boolean {
      const nearest = sortRows(this.visibleRows, 'air', {}).slice(0, EXACT_LIMIT);
      return nearest.length > 0 && nearest.every((row) => row._id in this.currentRoutes);
    },
    /** Everything that changes the result set; a change searches again (after the first search) */
    searchSignature(): string {
      return JSON.stringify([this.kind, this.originPlace?.location?.coordinates, this.originEntity?.id, this.isArea ? null : this.radiusKm,
        this.areaPolygon, this.institutionFilter, this.employeeFilter]);
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
    },
    setKind(kind: MapSearchKind) {
      this.kind = kind;
      this.sortBy = 'air';
      this.rows = [];
      this.searched = false;
      this.highlightId = null;
    },
    undoLast,
    emptyDraft,
    setSearchMode(mode: 'radius' | 'area') {
      this.searchMode = mode;
    },
    /** "Aus Umkreis starten": the current circle becomes an editable octagon */
    startFromCircle() {
      const coords = this.originPlace?.location?.coordinates;
      if (coords) {
        this.areaDraft = draftFromCircle([coords[0], coords[1]], this.radiusKm * 1000);
      }
    },
    /** V5: opens "Akquiseliste anlegen" with the drawn area (or the radius as polygon) as search area */
    openCreateList() {
      const coords = this.originPlace?.location?.coordinates;
      const polygon = this.isArea ? this.areaPolygon : coords ? draftPolygon(draftFromCircle([coords[0], coords[1]], this.radiusKm * 1000, 16)) : null;
      if (!polygon) {
        return;
      }
      this.presetArea = polygon;
      this.createListUsed = true;
      this.showCreateList = true;
    },
    /** V8: settings of the last search (never the address or chosen entities); access is checked again on every search */
    applyPreference() {
      const pref = readMapSearchPreference(this.$store.state.loginUserId);
      if (pref.kind && (pref.kind === 'institution' ? this.access.institutions : this.access.employees)) {
        this.kind = pref.kind;
      }
      if (pref.originMode) {
        this.originMode = pref.originMode;
      }
      this.searchMode = pref.searchMode || this.searchMode;
      this.radiusKm = pref.radiusKm || this.radiusKm;
      this.institutionTravelMode = pref.institutionTravelMode || this.institutionTravelMode;
      this.employeeTravelMode = pref.employeeTravelMode || this.employeeTravelMode;
      if (pref.institutionActive !== undefined) {
        this.institutionFilter.isActive = pref.institutionActive;
      }
      if (pref.institutionTypes) {
        this.institutionFilter.type = pref.institutionTypes;
      }
      if (pref.acquisitionStatus) {
        this.institutionFilter.acquisitionStatus = pref.acquisitionStatus;
      }
      if (pref.coverage) {
        this.coverageFilter = pref.coverage;
      }
      if (pref.employeeActive !== undefined) {
        this.employeeFilter.isActive = pref.employeeActive;
      }
      if (pref.subjects) {
        this.employeeFilter.subjects = pref.subjects as unknown as Types.ObjectId[];
      }
      (['hasCar', 'hasDriverLicense', 'hasLogin'] as const).forEach((key) => {
        if (pref[key] !== undefined) {
          this.employeeFilter[key] = pref[key] as boolean | null;
        }
      });
    },
    setOriginMode(mode: OriginMode) {
      this.originMode = mode;
      this.originError = '';
      if (mode !== 'address') {
        this.originEntity = null;
        this.originInstitution = undefined;
        this.originEmployee = undefined;
      }
    },
    setTravelMode(mode: MapTravelMode) {
      if (this.kind === 'institution') {
        this.institutionTravelMode = mode;
      } else {
        this.employeeTravelMode = mode;
      }
      if (this.sortBy === 'time' && !this.exactDone) {
        this.sortBy = 'air';
      }
    },
    /** V2: loads name and address of the chosen Standort / teacher */
    async loadOriginEntity(id: Types.ObjectId | undefined, type: CrudEntityTypes.INSTITUTION | CrudEntityTypes.EMPLOYEE) {
      this.originEntity = null;
      this.originError = '';
      if (!id) {
        return;
      }
      try {
        const isInstitution = type === CrudEntityTypes.INSTITUTION;
        const entity: any = isInstitution
            ? await InstitutionerApi.findById(id, {fields: ['name', 'address']})
            : await EmployeeHandlerAPI.findById(id, {fields: ['firstName', 'lastName', 'address']});
        if (!entity?.address?.location?.coordinates?.length) {
          this.originError = isInstitution ? 'Dieser Standort hat keine Adresse mit Koordinaten.' : 'Für diese Lehrkraft ist keine Wohnadresse mit Koordinaten hinterlegt.';
          return;
        }
        const name = isInstitution ? entity.name : `${entity.firstName || ''} ${entity.lastName || ''}`.trim();
        const wasSearchable = this.canSearch;
        this.originEntity = {id: String(id), type, name: name || 'Ohne Namen', address: entity.address};
        // A first origin searches at once; otherwise the canSearch / searchSignature watchers search again
        if (!this.hadSearched || wasSearchable) {
          this.$nextTick(() => this.search());
        }
      } catch (e) {
        this.originError = 'Die Adresse konnte nicht geladen werden.';
        this.$$showSnackbar('Die Adresse des Ausgangspunkts konnte nicht geladen werden', 'error', e);
      }
    },
    /** The pin was moved: the search starts at the new position (as free address) */
    async pinMoved(position: { lat: number; lng: number }) {
      const location = {type: 'Point', coordinates: [position.lng, position.lat]};
      let place: Place | null = null;
      try {
        const resp = await GeocoderAPI.findAddress({$search: `${position.lat},${position.lng}`, requireHouseNumber: false, requireStreet: false} as any);
        place = resp.items.length ? resp.items[0].value : null;
      } catch (e) {
        // no address found: keep the position without address text
      }
      this.originMode = 'address';
      this.originEntity = null;
      this.originError = '';
      this.address = place?.location ? place : {location} as unknown as Place;
      if (place?.location) {
        this.pinnedAddress = place;
      }
      this.search();
    },
    near(): Record<string, unknown> {
      const coords = this.originPlace?.location?.coordinates as number[];
      const near: Record<string, unknown> = {lat: coords[1], lon: coords[0], maxDistance: this.radiusKm * 1000};
      // With a drawn area the institutioner uses near only as origin (no radius)
      // With an entity as origin the services use its stored address (and the distance cache of the geocoder)
      if (this.originMode !== 'address' && this.originEntity) {
        near.originId = this.originEntity.id;
        near.originType = this.originEntity.type;
      }
      return near;
    },
    rowAccess(): RowAccess {
      const a = this.access;
      return {coverage: a.coverage, status: a.acquisitionStatus, hasCar: a.hasCar, subjects: a.subjects, assigned: a.assignedCount, maxDistance: a.maxDistance};
    },
    institutionQueryFilter(): Record<string, unknown> {
      const f = this.institutionFilter;
      const filter: Record<string, unknown> = {near: this.near()};
      if (this.areaPolygon) {
        filter.withinArea = this.areaPolygon;
      }
      if (this.access.institutionActive && f.isActive !== 'all') {
        filter.isActive = f.isActive;
      }
      if (this.access.institutionType && f.type.length) {
        filter.type = f.type;
      }
      if (this.access.acquisitionStatus && f.acquisitionStatus.length) {
        filter.acquisitionStatus = f.acquisitionStatus;
      }
      return filter;
    },
    employeeQueryFilter(): Record<string, unknown> {
      const f = this.employeeFilter;
      const a = this.access;
      const filter: Record<string, unknown> = {isTeacher: true, near: this.near()};
      if (a.employeeActive && f.isActive !== 'all') {
        filter.isActive = f.isActive;
      }
      if (a.subjects && f.subjects.length) {
        filter.subjects = f.subjects;
      }
      if (a.hasCar && f.hasCar !== null) {
        filter.hasCar = f.hasCar;
      }
      if (a.driverLicense && f.hasDriverLicense !== null) {
        filter.hasDriverLicense = f.hasDriverLicense;
      }
      if (a.hasLogin && f.hasLogin !== null) {
        filter.hasLogin = f.hasLogin;
      }
      if (a.assignedInstitutions && f.assignedInstitutions.length) {
        filter.assignedInstitutions = f.assignedInstitutions;
      }
      if (a.plannedInstitutions && f.plannedInstitutions.length) {
        filter.plannedInstitutions = f.plannedInstitutions;
      }
      if (a.acquiredBy && f.acquiredBy.length) {
        filter.acquiredBy = f.acquiredBy;
      }
      return filter;
    },
    /** V1: air-line search without Google */
    async search() {
      window.clearTimeout(this.researchTimer);
      const origin = this.originPlace;
      if (!origin || !this.canSearch) {
        return;
      }
      const run = ++this.searchRun;
      const access = this.rowAccess();
      this.loading = true;
      try {
        let rows: MapRow[];
        if (this.kind === 'institution') {
          const fields = ['name', 'address'];
          if (access.coverage) {
            fields.push('assignedTeachers', 'plannedTeachers');
          }
          if (access.status) {
            fields.push('acquisitionStatus');
          }
          const resp = await InstitutionerApi.find({filter: this.institutionQueryFilter(), fields, skipPagination: true} as any);
          rows = ((resp?.institutions || []) as any[]).map((institution) => institutionRow(institution, origin, access));
        } else {
          const fields = ['firstName', 'lastName', 'address'];
          if (access.hasCar) {
            fields.push('hasCar');
          }
          if (access.subjects) {
            fields.push('subjects');
          }
          if (access.assigned) {
            fields.push('assignedInstitutions');
          }
          if (access.maxDistance) {
            fields.push('maxDistance');
          }
          const resp = await EmployeeHandlerAPI.find({
            filter: this.employeeQueryFilter(),
            fields,
            populate: access.subjects ? {subjects: {fields: ['name']}} : undefined,
            skipPagination: true,
          } as any);
          rows = ((resp?.employees || []) as any[]).map((employee) => employeeRow(employee, origin, access));
        }
        if (run !== this.searchRun) {
          return;
        }
        // The origin itself is no result
        const originId = this.originMode !== 'address' ? this.originEntity?.id : undefined;
        this.rows = originId ? rows.filter((row) => row._id !== originId) : rows;
        this.searched = true;
        this.hadSearched = true;
        if (this.sortBy === 'time' && !this.exactDone) {
          this.sortBy = 'air';
        }
      } catch (e) {
        if (run === this.searchRun) {
          this.$$showSnackbar(`Die ${this.kind === 'institution' ? 'Standorte' : 'Lehrkräfte'} konnten nicht gesucht werden. Bitte erneut versuchen.`, 'error', e);
        }
      } finally {
        if (run === this.searchRun) {
          this.loading = false;
        }
      }
    },
    /** Routes via Google for the nearest results (on request) */
    async calculateExact() {
      if (!this.canUseDistanceMatrix || !this.originPlace) {
        return;
      }
      const key = this.routeKey;
      const ids = sortRows(this.visibleRows, 'air', {}).slice(0, EXACT_LIMIT).map((row) => row._id);
      if (!ids.length) {
        return;
      }
      this.calculating = true;
      try {
        const near: Record<string, unknown> = {...this.near(), useExactDistance: true};
        let found: any[];
        if (this.kind === 'institution') {
          near.useTravelMode = this.travelMode;
          // withinArea keeps schools of the area that lie outside the radius
          const resp = await InstitutionerApi.find({filter: {_id: ids, near, withinArea: this.areaPolygon || undefined}, fields: ['_id', 'address'],
            skipPagination: true} as any);
          found = resp?.institutions || [];
        } else {
          if (this.travelMode === 'own') {
            near.considerTravelMode = true;
          } else {
            near.useTravelMode = this.travelMode;
          }
          const resp = await EmployeeHandlerAPI.find({filter: {_id: ids, near}, fields: ['_id', 'address', 'hasCar'], skipPagination: true} as any);
          found = resp?.employees || [];
        }
        const info: Record<string, RouteInfo | null> = {...(this.routes[key] || {})};
        ids.forEach((id) => {
          info[id] = null;
        });
        for (const item of found) {
          if (typeof item.distance === 'number' && item.distance >= 0) {
            info[String(item._id)] = {distance: item.distance, duration: item.duration, travelMode: item.travelMode};
          }
        }
        this.$set(this.routes, key, info);
        this.sortBy = 'time';
      } catch (e) {
        this.$$showSnackbar('Die Fahrzeiten konnten nicht berechnet werden', 'error', e);
      } finally {
        this.calculating = false;
      }
    },
    selectFromMap(id: string) {
      this.highlightId = id;
      (this.$refs.results as any)?.reveal(id);
    },
    openCard(row: MapRow) {
      if (row.kind === 'institution') {
        this.cardInstitutionId = row._id as unknown as Types.ObjectId;
        this.showInstitutionCard = true;
      } else {
        this.cardEmployeeId = row._id as unknown as Types.ObjectId;
        this.showEmployeeCard = true;
      }
    },
  },
  watch: {
    originInstitution(id: Types.ObjectId | undefined) {
      if (this.originMode === 'institution') {
        this.loadOriginEntity(id, CrudEntityTypes.INSTITUTION);
      }
    },
    originEmployee(id: Types.ObjectId | undefined) {
      if (this.originMode === 'employee') {
        this.loadOriginEntity(id, CrudEntityTypes.EMPLOYEE);
      }
    },
    /** After the first search every change updates the results (air-line only, no Google costs) */
    searchSignature() {
      if (!this.searched || !this.originPlace) {
        return;
      }
      window.clearTimeout(this.researchTimer);
      this.researchTimer = window.setTimeout(() => this.search(), RESEARCH_DELAY_MS);
    },
    preference: {
      deep: true,
      handler(preference: MapSearchPreference) {
        if (this.preferencesLoaded) {
          writeMapSearchPreference(this.$store.state.loginUserId, preference);
        }
      },
    },
    /** Without a complete origin / area the old results would be misleading: clear them, search again once complete */
    canSearch(can: boolean) {
      window.clearTimeout(this.researchTimer);
      if (!can) {
        this.searchRun++;
        this.rows = [];
        this.searched = false;
        this.loading = false;
        this.highlightId = null;
      } else if (this.hadSearched) {
        this.search();
      }
    },
    /** V4: a finished area searches at once (air-line, no Google costs) */
    areaPolygon(polygon: GeoJsonPolygon | null) {
      // after an earlier search the canSearch watcher searches
      if (polygon && this.originPlace && !this.hadSearched) {
        this.search();
      }
    },
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open && !this.preferencesLoaded) {
          this.applyPreference();
          this.$nextTick(() => {
            this.preferencesLoaded = true;
          });
        }
        // Without access to Standorte the search starts with teachers
        if (open && !this.access.institutions && this.access.employees) {
          this.kind = 'employee';
        }
      },
    },
  },
  beforeDestroy() {
    window.clearTimeout(this.researchTimer);
  },
});
</script>

<style scoped src="@/components/acquisitionList/create/createAcquisitionListDialog.css"></style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/acquisitionList/ui/itemDetails.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>

<style scoped src="./gmapsSearchDialog.css"></style>
